import type { MarkType, Node, Slice } from "prosemirror-model";
import type { EditorState, Transaction } from "prosemirror-state";
import { NodeSelection } from "prosemirror-state";
import type { Transform } from "prosemirror-transform";

import type { RichTextCellType } from "../../../../../shared/types/worksheet";

import {
  type TextblockAttrs,
  getTextblockAttrs,
  isColorMark,
  isFormulaNode,
  isLinkMark,
  schema,
} from "./adapter/schema";
import type { AuthoredTransaction, RemoteSelectionData } from "./types";

import { t } from "i18n/i18n";
import { compactMap } from "utils/collections";
import { isValidUrl } from "utils/urls";

export type OpWithIndex = { index: number; op: RichTextCellType["ops"][number] };

export const ALLOWED_IMAGE_MIME_TYPES: Record<string, string> = {
  JPEG: "image/jpeg",
  PNG: "image/png",
  GIF: "image/gif",
  WebP: "image/webp",
};

export const isAllowedImageType = (mimeType: string): boolean =>
  Object.values(ALLOWED_IMAGE_MIME_TYPES).includes(mimeType);

/** Returns true if the given LaTeX string causes a KaTeX render error. */
export function latexHasError(latex: string): boolean {
  if (!window.katex) return false;

  try {
    window.katex.renderToString(latex, { throwOnError: true });
    return false;
  } catch {
    return true;
  }
}

/**
 * Get the shared block attributes (align, indent) from a node.
 * Used when changing a node's type to preserve these attrs across the conversion
 * while discarding type-specific attrs (e.g., level on headings).
 */
export function getSharedAttrs(node: Node): TextblockAttrs {
  const { align, indent } = getTextblockAttrs(node);
  return { align, indent };
}

/**
 * Strip ops missing "insert" or containing "retain" from stored doc data.
 * Returns valid and invalid ops with index.
 */
export function partitionOps(ops: RichTextCellType["ops"] = []): {
  validOps: OpWithIndex[];
  invalidOps: OpWithIndex[];
} {
  const invalidIndexes = ops.reduce<number[]>((acc, op, index) => {
    if (op.insert == null || op.retain) acc.push(index);
    return acc;
  }, []);

  if (invalidIndexes.length === 0) {
    return {
      validOps: ops.map((op, index) => ({ index, op })),
      invalidOps: [],
    };
  }

  const invalidIndexSet = new Set(invalidIndexes);
  const validOps = compactMap(ops, (op, index) =>
    invalidIndexSet.has(index) ? null : { index, op },
  );
  const invalidOps = invalidIndexes.map((index) => ({ index, op: ops[index] }));

  return { validOps, invalidOps };
}

/**
 * Merge adjacent code_block nodes in a Transform, combining their content with embedded \n.
 * Uses join + insert rather than replaceWith so that cursor positions map more naturally
 * into the merged block instead of jumping to the end.
 */
export function mergeAdjacentCodeBlocks(tr: Transform) {
  let joinPos = tr.doc.content.size;
  for (let i = tr.doc.childCount - 1; i > 0; i -= 1) {
    const cur = tr.doc.child(i);
    const prev = tr.doc.child(i - 1);
    joinPos -= cur.nodeSize;
    if (cur.type === schema.nodes.code_block && prev.type === schema.nodes.code_block) {
      tr.join(joinPos, 1);
      tr.insert(joinPos - 1, schema.text("\n"));
      // The merge absorbs cur into prev (+ cur.nodeSize) but removes 2 boundary
      // positions and adds 1 newline (net −1). Advance joinPos to the end of the
      // merged block so the next iteration's subtraction yields the correct boundary.
      joinPos += cur.nodeSize - 1;
    }
  }
}

/**
 * Check if a mark (formatting like bold, italic, code) is active in the current selection
 *
 * Marks can be applied to:
 * - A text selection (when text is selected)
 * - The cursor position (stored marks, applied to the next character typed)
 */
export function markActive(state: EditorState, type: MarkType): boolean {
  const { from, $from, to, empty } = state.selection;
  // If selection is empty (just a cursor), check stored marks or marks at cursor position
  if (empty) return !!type.isInSet(state.storedMarks || $from.marks());
  // If text is selected, check if the mark exists anywhere in the selected range
  return state.doc.rangeHasMark(from, to, type);
}

/**
 * Returns the single hex color shared across the selection, or null if mixed or no color is specified.
 * Uses nodesBetween to avoid leaking boundary marks from adjacent characters, unlike ResolvedPos.marks().
 */
export function getSelectionColor(state: EditorState, from: number, to: number): string | null {
  const colors = new Set<string | null>();
  state.doc.nodesBetween(from, to, (node) => {
    if (colors.size > 1) return false; // skip descent into this node's children
    if (node.isText) {
      const mark = node.marks.find(isColorMark);
      colors.add(mark ? mark.attrs.color : null);
    }
  });
  if (colors.size !== 1) return null;
  const [color] = colors;
  return color;
}

/**
 * Check if either end of the current selection is inside a code_block node.
 * Used to disable formatting toolbar buttons and keyboard shortcuts that
 * should not apply within code blocks.
 */
export function isSelectionInCodeBlock(state: EditorState): boolean {
  const { $from, $to } = state.selection;
  return (
    $from.parent.type === schema.nodes.code_block || $to.parent.type === schema.nodes.code_block
  );
}

/**
 * Check if the selection is a NodeSelection on a formula node.
 * Used to disable toolbar buttons that should not apply to formulas.
 */
export function isFormulaSelected(state: EditorState): boolean {
  return state.selection instanceof NodeSelection && isFormulaNode(state.selection.node);
}

/**
 * Check if the selection is a NodeSelection on a formula node with a KaTeX render error.
 * Used to disable toolbar buttons that should not apply to formulas.
 */
export function isFormulaErrorSelected(state: EditorState): boolean {
  if (!(state.selection instanceof NodeSelection)) return false;
  const { node } = state.selection;
  if (!isFormulaNode(node)) return false;
  return latexHasError(node.attrs.formula);
}

/**
 * Check if the selection is a NodeSelection on an image node.
 * Used to disable toolbar buttons that should not apply to images.
 */
export function isImageSelected(state: EditorState): boolean {
  return state.selection instanceof NodeSelection && state.selection.node.type.name === "image";
}

/**
 * Maps remote cursor positions through transaction mappings to keep them visually stable when
 * the document changes. Called for both local and remote document updates.
 *
 * ShareDB delivers presence (cursor positions) and ops (content changes) separately.
 * Without remapping, a doc change can make stored positions stale, causing remote cursors to jump
 * until the next presence update corrects them. Presence updates are authoritative and always
 * overwrite these mapped positions when they arrive.
 *
 * Returns a new map with updated selections merged in, or null if no positions changed.
 */
export function remapSelections(
  selections: Map<string, RemoteSelectionData>,
  authoredTransactions: AuthoredTransaction[],
): Map<string, RemoteSelectionData> | null {
  if (selections.size === 0 || authoredTransactions.length === 0) return null;

  // Find the earliest position affected by any step across all transactions
  let earliestChangePos = Infinity;
  for (const { transaction } of authoredTransactions) {
    for (const step of transaction.steps) {
      if ("from" in step && typeof step.from === "number") {
        earliestChangePos = Math.min(earliestChangePos, step.from);
      }
    }
  }

  // No positional changes so no need to map selections
  if (earliestChangePos === Infinity) return null;

  const updatedSelections = new Map<string, RemoteSelectionData>();
  for (const [srcPresenceId, selection] of selections) {
    // Remap if selection extends to or past the change position
    // (selections entirely before the change don't need remapping)
    if (selection.to >= earliestChangePos) {
      // Map through each transaction individually so we can apply per-author bias.
      // Bias only matters when a cursor is at the exact insertion point: the op
      // author's cursor should follow their own typing (bias 1), but bystanders'
      // cursors should stay put before the new content (bias -1). For positions
      // before or after the insertion, both biases produce the same result.
      let mappedFrom = selection.from;
      let mappedTo = selection.to;
      for (const { transaction, authorUserId } of authoredTransactions) {
        const bias = authorUserId && selection.userId === authorUserId ? 1 : -1;
        mappedFrom = transaction.mapping.map(mappedFrom, bias);
        mappedTo = transaction.mapping.map(mappedTo, bias);
      }

      // Only update if positions changed (may collapse to a point if content was deleted)
      if (mappedFrom !== selection.from || mappedTo !== selection.to) {
        updatedSelections.set(srcPresenceId, { ...selection, from: mappedFrom, to: mappedTo });
      }
    }
  }

  if (updatedSelections.size === 0) return null;
  return new Map([...selections, ...updatedSelections]);
}

/** Trims whitespace and ensures a URL has a protocol. If the value doesn't contain "://", prepends "https://". */
export function transformLinkUrl(val: string): string {
  const trimmed = val.trim();
  if (trimmed && !trimmed.includes("://")) {
    return `https://${trimmed}`;
  }
  return trimmed;
}

/**
 * Validates a URL string. Returns null if valid, or an error message if not.
 * Accepts only http and https protocols.
 */
export function validateLinkUrl(val: unknown): string | null {
  if (typeof val !== "string" || /\s/.test(val)) {
    return t("validation.invalid_url");
  }
  try {
    const parsed = new URL(val);
    if (!["http:", "https:"].includes(parsed.protocol)) {
      return t("validation.http_https_only");
    }
    return null;
  } catch {
    return t("validation.invalid_url");
  }
}

/**
 * Adds a link mark and strips any color mark from the same range, so the default
 * link color shows through when a link is applied over colored text.
 */
export function addLinkMark(tr: Transaction, from: number, to: number, href: string): Transaction {
  return tr
    .addMark(from, to, schema.marks.link.create({ href }))
    .removeMark(from, to, schema.marks.color);
}

/**
 * Extracts the first URL from pasted content.
 * Prefers a link mark's href from the parsed ProseMirror slice (handles pasting a formatted
 * hyperlink). Falls back to the clipboard's plain text if it is a valid URL (handles pasting
 * a raw URL string).
 */
export function extractUrlFromPastedContent(
  slice: Slice,
  event: ClipboardEvent,
): string | undefined {
  let firstURL: string | undefined;
  slice.content.descendants((node) => {
    if (firstURL) return false;
    const linkMark = node.marks.find(isLinkMark);
    if (linkMark?.attrs.href) firstURL = linkMark.attrs.href;
  });

  if (!firstURL) {
    const text = event.clipboardData?.getData("text");
    if (text && isValidUrl(text)) firstURL = text;
  }

  return firstURL;
}
